<script setup lang="ts">
import type { BaseNotificationDialogProps } from '@nimbus/types'
import { PresentationType } from '@core/types'
import { useNotificationDialogs, useNotifications, useI18n, computed, ref, useIsPreview, navigateTo, ApiError, OpenapiError, isServerError, DIALOG_SEQUENCE_TOAST_TYPE, DIALOG_STEP_PRIORITY } from '#imports'
import type { Name as ThemeName } from '#theme'
import type { NotificationAcceptVariant, NotificationMessageButtonProps } from './MessageButton.vue'

export interface NotificationDefaultMessageDialogProps extends BaseNotificationDialogProps {
    /**
     * Render the dialog under a specific theme. Defaults to `undefined`, which
     * leaves the dialog on its inherited/default theme.
     *
     * @default
     * undefined
     */
    theme?: ThemeName
    hideActions?: boolean
    busy?: boolean
    /**
     * Mark the step required: it shows the sequence's Required label and lock
     * note and is answered through its own buttons, never closed.
     *
     * @default
     * false
     */
    required?: boolean
    /**
     * Hide the close icon without making the dialog persistent, for
     * notifications the player must answer.
     *
     * @default
     * false
     */
    hideCloseIcon?: boolean
    /**
     * How the accept button is painted.
     *
     * @default
     * 'cta'
     */
    acceptVariant?: NotificationAcceptVariant
    /**
     * Enable a gap in content of 16px.
     *
     * @default
     * true
     */
    gap?: boolean
}
const props = withDefaults(
    defineProps<NotificationDefaultMessageDialogProps>(),
    {
        gap: true
    }
)

// `opened` fires once, the first time the step opens, and never in preview.
const emit = defineEmits<{ closed: [], opened: [] }>()

const { t } = useI18n()

const isPreview = useIsPreview()
const { markAsShown, useCallAction, markAsRead } = useNotificationDialogs({ enabled: false })

// Persistent dialogs stay until the user acts on them, so they never offer a close icon.
const isPersistent = computed(() => props.notification.presentation_type === PresentationType.persistent_dialog)

const open = ref(true)
const shownTracked = ref(false)

const notifications = useNotifications()
const closing = ref(false)

// The sheet's X: the notification closes once it's marked read, and stays up for a retry if that fails.
function closeSheet() {
    open.value = false
}

function onCloseIconClick() {
    if (isPreview) return closeSheet()

    closing.value = true
    markAsRead.mutate({ notifications: [props.notification] }, {
        onSuccess: closeSheet,
        onError: () => {
            notifications.add({ type: DIALOG_SEQUENCE_TOAST_TYPE, value: { message: t('core.defaultErrorMessage'), type: 'danger' }, multiple: true })
        },
        onSettled: () => {
            closing.value = false
        }
    })
}

// Service-written bodies link to external pages (e.g. the regulator); following one in place would leave the site mid-dialog.
function openExternalLinksInNewTab(event: MouseEvent) {
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null

    if (!(anchor instanceof HTMLAnchorElement)) return
    if (!['http:', 'https:'].includes(anchor.protocol) || anchor.origin === window.location.origin) return

    anchor.target = '_blank'
    anchor.rel = 'noopener noreferrer'
}

// Only a message the service wrote for the player, in its own error format: a service `code` (e.g. KYC:1001).
// A fetch error's own message is the request URL and status, and a bare framework body is English internals
// ("Server Error", "Unauthenticated.", "Too Many Attempts.").
function getErrorMessage(error: ApiError | OpenapiError | Error | null | undefined): string | undefined {
    if (isServerError(error)) return undefined
    if (!ApiError.isError(error) && !OpenapiError.isError(error)) return undefined

    return typeof error.data?.code === 'string' ? error.data.message : undefined
}

const actionErrorMessage = computed(() => getErrorMessage(accept.error.value) || getErrorMessage(dismiss.error.value))

const accept = useCallAction()
async function onAccept(close: () => void) {
    if (!props.notification.acceptAction) return
    if (closing.value) return
    if (isPreview) {
        // Previews skip the callback but still follow the redirect so the real target page shows.
        if (props.notification.acceptAction.redirect) await navigateTo(props.notification.acceptAction.redirect, { external: true })
        return
    }

    await accept.mutate({
        action: props.notification.acceptAction,
        notification: props.notification
    }, {
        onMutate: props.actions?.accept
            ? () => props.actions?.accept?.()
            : undefined,
        onSuccess: async () => {
            if (isPersistent.value) {
                await markAsRead.mutateAsync({ notifications: [props.notification] })
            }
            close()
        }
    })
}

const dismiss = useCallAction()
async function onDismiss(close: () => void) {
    if (!props.notification.dismissAction) return
    if (closing.value) return
    if (isPreview) {
        if (props.notification.dismissAction.redirect) await navigateTo(props.notification.dismissAction.redirect, { external: true })
        return
    }

    await dismiss.mutate({
        action: props.notification.dismissAction!,
        notification: props.notification
    }, {
        onMutate: props.actions?.dismiss ? () => props.actions?.dismiss?.() : undefined,
        onSuccess: async () => {
            if (isPersistent.value) {
                await markAsRead.mutateAsync({ notifications: [props.notification] })
            }
            close()
        }
    })
}

function makeSlotContext(close: () => void): {
    close: () => void
    /** The X is marking the notification read: slot actions must wait. */
    closing: boolean
    actions: {
        accept: NotificationMessageButtonProps & { onClick: () => Promise<void> }
        dismiss: NotificationMessageButtonProps & { onClick: () => Promise<void> }
    }
} {
    return {
        close,
        closing: closing.value,
        actions: {
            accept: {
                actionType: 'accept',
                action: props.notification.acceptAction,
                acceptVariant: props.acceptVariant,
                pending: accept.pending.value,
                onClick: () => onAccept(close)
            },
            dismiss: {
                actionType: 'dismiss',
                action: props.notification.dismissAction,
                pending: dismiss.pending.value,
                onClick: () => onDismiss(close)
            }
        }
    }
}

// Once: the sequence opens the step again when an interrupting step hands the sheet back.
function onOpened() {
    if (isPreview || shownTracked.value) return
    shownTracked.value = true
    setTimeout(() => {
        markAsShown.mutate(props.notification)

        if (props.notification.presentation_type === PresentationType.dialog) {
            markAsRead.mutate({ notifications: [props.notification], silent: true })
        }
    }, 1000)
    emit('opened')
}
</script>

<template>
    <UIDialogStep
        v-slot="{ close }"
        v-model="open"
        name="notification"
        :priority="DIALOG_STEP_PRIORITY.notification"
        :close-on-backdrop-click="false"
        :close-icon="!isPersistent && !props.hideCloseIcon"
        :required="props.required"
        :close-on-close-icon-click="false"
        :busy="accept.pending.value || dismiss.pending.value || closing || !!props.busy"
        :theme="props.theme"
        @close-icon-click="onCloseIconClick"
        @opened="onOpened"
        @closed="emit('closed')"
    >
        <div
            class="notification-dialog-default-message"
            :class="{
                'notification-dialog-default-message--with-gap': props.gap
            }"
            @click.capture="openExternalLinksInNewTab"
        >
            <Transition name="slide-up-inline">
                <LazyUIAlert
                    v-if="accept.error.value || dismiss.error.value"
                    class="notification-dialog-default-message__alert"
                    type="warning"
                    :message="actionErrorMessage || t('core.defaultErrorMessage')"
                />
            </Transition>

            <slot name="title.before" />

            <slot name="title">
                <h4 class="notification-dialog-default-message__title">
                    {{ props.notification.title }}
                </h4>
            </slot>

            <slot name="body" v-bind="makeSlotContext(close)">
                <div v-if="props.notification.body" class="notification-dialog-default-message__body">
                    <UISafeHtml :html="props.notification.body" />
                </div>
            </slot>

            <slot
                name="body.after"
                v-bind="makeSlotContext(close)"
            />

            <slot
                v-if="!props.hideActions"
                name="actions"
                v-bind="makeSlotContext(close)"
            >
                <div class="notification-dialog-default-message__actions">
                    <NimbusNotificationDialogMessageButton
                        v-if="props.notification.acceptAction"
                        action-type="accept"
                        :action="props.notification.acceptAction"
                        :accept-variant="props.acceptVariant"
                        :pending="accept.pending.value"
                        @click="() => onAccept(close)"
                    />

                    <NimbusNotificationDialogMessageButton
                        v-if="props.notification.dismissAction"
                        action-type="dismiss"
                        :action="props.notification.dismissAction"
                        :pending="dismiss.pending.value"
                        @click="() => onDismiss(close)"
                    />
                </div>
            </slot>
        </div>
    </UIDialogStep>
</template>

<style scoped lang="scss">
.notification-dialog-default-message {
    display: grid;

    &--with-gap {
        gap: 16px;
    }

    &__alert.alert {
        margin-bottom: unset;
    }

    // Without the gap, nothing else spaces the alert from the content below it.
    &:not(.notification-dialog-default-message--with-gap) &__alert.alert {
        margin-bottom: 16px;
    }

    &__actions {
        display: grid;
        gap: 8px;
        margin-top: 18px;
    }
}

// Same type as UISheetPage (the other dialog-sequence steps): the slotted variants share these classes.

.notification-dialog-default-message__title,
:slotted(.notification-dialog-default-message__title) {
    margin: 0;
    color: var(--on-surface, $dialog-font-color);
    font-size: $font-size-xl;
    font-weight: $text-bold;
    line-height: 1.25;
    text-wrap: balance;
}

.notification-dialog-default-message__body,
:slotted(.notification-dialog-default-message__body) {
    margin-top: 10px;
    color: $dialog-text-muted;
    font-size: $font-size-sm;
    line-height: 1.6;
}
</style>
